<template>
  <div class="chatbot-wrapper">
    <div class="chatbot">
      <div v-if="messages.length" ref="content" class="scrollable-content">
        <Node v-for="(conv, index) in messages" :key="index">
          <ErrorMessage v-if="isErrorMessage(conv.messageType)" :message="conv.message" />
          <ContextMessage v-else-if="isContextMessage(conv.messageType)" :message="conv" />
          <Message v-else :message="conv" />
        </Node>
      </div>
      <div v-else class="no-messages-title">
        <div class="icon">
          <img :src="productIcon" alt="product" />
        </div>
        <div>{{ t('conversation.whatCanIHelp') }}</div>
      </div>

      <!-- Scroll to bottom button -->
      <transition name="fade">
        <BbBtn
          v-if="showScrollButton"
          class="scroll-to-bottom-btn"
          type="icon"
          icon="chevron-down"
          icon-size="medium"
          :aria-label="t('conversation.scrollToBottom')"
          @click="scrollToBottom"
        />
      </transition>
    </div>
    <div
      class="operations-container"
      v-has-access:hide="{ resource: RESOURCE_TYPE.EXTENDED_THINKING, resource_action: RESOURCE_ACTION_TYPE.CREATE }"
    >
      <div class="shared-info-message" @mouseenter="showTooltip = true" @mouseleave="showTooltip = false">
        <ResearchModeInfoTooltip
          v-if="showTooltip"
          :title="$t('assist.researchModeInfoTitle')"
          :description="$t('assist.researchModeInfoMessage')"
        />
        <BbIcon size="medium" name="information" type="filled" />
      </div>
      <ButtonWithIcon
        :icon="isResearchModeOn ? 'checkmark-small' : ''"
        :selected="isResearchModeOn"
        text-key="assist.researchMode"
        @click="handleIsResearchModeOn"
      />
    </div>
    <MessageInput @onSubmit="onSubmitValue" />
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import productIcon from '~/assets/svg/product-icon.svg';

import { DEFAULT_COLLECTION, messageType, queryType } from '~/constants/conversationModels';
import { RESOURCE_TYPE, RESOURCE_ACTION_TYPE } from '~/constants/user';

import Message from '~/components/conversation/chatMessage/Message';
import Node from '~/components/conversation/chatMessage/Node';
import MessageInput from '~/components/conversation/MessageInput';
import useConversations from '~/composables/useConversations';
import ContextMessage from '~/components/conversation/chatMessage/ContextMessage';
import ErrorMessage from '~/components/conversation/chatMessage/ErrorMessage';
import ButtonWithIcon from '~/components/conversation/ButtonWithIcon.vue';
import ResearchModeInfoTooltip from '~/components/conversation/chatMessage/ResearchModeInfoTooltip.vue';
import { useStreamingScroll } from '~/composables/useStreamingScroll';

export default {
  name: 'Conversation',
  computed: {
    RESOURCE_TYPE() {
      return RESOURCE_TYPE;
    },
    RESOURCE_ACTION_TYPE() {
      return RESOURCE_ACTION_TYPE;
    },
  },
  components: {
    ContextMessage,
    MessageInput,
    Message,
    Node,
    ErrorMessage,
    ButtonWithIcon,
    ResearchModeInfoTooltip,
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();

    const {
      createNewConversation,
      currentConversation,
      publishToConversation,
      setIsNewConversation,
      isResearchModeOn,
      setIsResearchModeOn,
    } = useConversations();
    const messages = computed(() => currentConversation.value.messages);
    const { content, showScrollButton, scrollToBottom, prepareForNewMessage, resetScrollState } =
      useStreamingScroll(messages);

    const conversationId = computed(() => currentConversation.value.conversationId);

    const conversationStack = ref([]);
    const showTooltip = ref(false);

    const isContextMessage = (type) => type === messageType.context;
    const isErrorMessage = (type) => type === messageType.error;

    const shouldShowScopeMessage = (message, index) => {
      if (index === 0 && message.collectionId === DEFAULT_COLLECTION.collectionId) {
        return false;
      }
      return index === 0 || messages.value[index - 1].collectionId !== message.collectionId;
    };

    const onSubmitValue = async (value) => {
      if (!route.params.conversationId) {
        setIsNewConversation(true);
        await createNewConversation();
      }
      prepareForNewMessage();
      await publishToConversation(conversationId.value, queryType.search, value);
    };

    const handleIsResearchModeOn = () => setIsResearchModeOn(!isResearchModeOn.value);

    watch(
      () => route.params.conversationId,
      () => resetScrollState(),
    );

    return {
      t,
      content,
      conversationStack,
      isContextMessage,
      isErrorMessage,
      messages,
      onSubmitValue,
      shouldShowScopeMessage,
      isResearchModeOn,
      handleIsResearchModeOn,
      productIcon,
      showTooltip,
      showScrollButton,
      scrollToBottom,
    };
  },
};
</script>

<style lang="scss" scoped>
.chatbot-wrapper {
  --primary-color: #{$bb-primary-1};
  --primary-dimmed-color: #{$bb-primary-1-dimmed};

  @include bb-base-font-family;
  @include bb-regular-text-styles('md');

  @include media-breakpoint-up('sm') {
    // padding: 0 156px $bb-spacing-4 0;
    // padding: 0 $bb-spacing-8 0 $bb-spacing-8;
  }

  height: 100%;

  color: $bb-neutral-1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: antialiased;
  font-smoothing: antialiased;
  -webkit-text-size-adjust: 100%; /* Prevent adjustments of font size after orientation changes in iOS */

  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  flex: 1;
  max-width: 1024px;
  margin: auto;
  padding: $bb-spacing-4;
  min-width: 0;

  .operations-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: $bb-spacing-1;
    min-height: $bb-spacing-6;

    .shared-info-message {
      display: flex;
      gap: $bb-spacing-2;
      padding: $bb-spacing-2;
      position: relative;
      cursor: pointer;

      :deep(span) {
        color: $bb-neutral-3 !important;
      }

      i {
        color: $bb-primary-3;
        font-size: 32px;
      }
    }
  }

  .chatbot {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: auto;
    justify-content: flex-end;
    position: relative;

    .no-messages-title {
      font-size: 38px !important;
      margin: auto;
      display: flex;
      align-items: center;
      gap: $bb-spacing-6;

      .icon {
        background: #fdb91a;
        border-radius: 100px;
        padding: $bb-spacing-2;
      }
    }
  }

  .scrollable-content {
    overflow: auto;
    max-height: 100%;
    padding-right: $bb-spacing-4;
  }

  .scroll-to-bottom-btn {
    position: absolute;
    bottom: $bb-spacing-4;
    left: 50%;
    transform: translateX(-50%);
    width: 40px !important;
    height: 40px !important;
    border-radius: 50%;
    background-color: $bb-neutral-7;
    border: 1px solid $bb-neutral-5;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    z-index: 10;

    &:hover {
      background-color: $bb-neutral-0 !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    &:active {
      transform: translateX(-50%) scale(0.95);
    }

    :deep(svg),
    :deep(i) {
      color: $bb-neutral-2;
    }
  }

  // Fade transition for the button
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
